import React, { useMemo, useState } from 'react';
import { Avatar, Box, Button, Grid, MenuItem, Select, TextField, Typography, Chip, Stack, useTheme } from '@mui/material';
import { useDispatch } from 'react-redux';
import MailIcon from '@mui/icons-material/Mail';
import PhoneIcon from '@mui/icons-material/Phone';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import WorkIcon from '@mui/icons-material/Work';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';
import { useAuth } from '../../Context/AuthContext';
import * as actions from '../../Reducers/Actions';
import Card from '../../SystemDesign/Card';
import { useUpdateUserMutation } from '../../services/SkillsManagement';
import { tokens } from '../../theme';

const safeDate = (value) => {
  if (!value) return '';
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '';
    // yyyy-mm-dd for input[type=date]
    return d.toISOString().slice(0, 10);
  } catch {
    return '';
  }
};

export default function Profile() {
  const { user, refreshUser } = useAuth();
  const dispatch = useDispatch();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const profile = user?.profile || {};
  const initials = useMemo(() => {
    const f = (profile.firstName || '').trim();
    const l = (profile.lastName || '').trim();
    return `${f?.[0] || ''}${l?.[0] || ''}`.toUpperCase() || (user?.username?.[0] || 'U');
  }, [profile.firstName, profile.lastName, user?.username]);

  const [isEditing, setIsEditing] = useState(false);
  const [updateUser, { isLoading: isSaving } ] = useUpdateUserMutation();
  const [formData, setFormData] = useState(() => ({
    firstName: profile.firstName || '',
    lastName: profile.lastName || '',
    email: user?.email || '',
    phone: profile.phone || '',
    bio: profile.bio || '',
    addresse: profile.addresse || '',
    gender: profile.gender || '',
    birthday: safeDate(profile.birthday),
    preferred_language: profile.preferred_language || 'fr',
  }));

  const handleChange = (key) => (e) => {
    setFormData((s) => ({ ...s, [key]: e?.target ? e.target.value : e }));
  };

  const handleSave = async () => {
    try {
      const payload = {
        body: {
          // username, roleId, isActive are optional; we only send profile updates from this screen
          profile: {
            firstName: formData.firstName || undefined,
            lastName: formData.lastName || undefined,
            phone: formData.phone || undefined,
            birthday: formData.birthday || undefined,
            gender: formData.gender || undefined,
            bio: formData.bio || undefined,
            addresse: formData.addresse || undefined,
            // profilePic could be added later if available
          },
        },
      };
      const result = await updateUser(payload).unwrap();

      // Update Redux list views immediately if present
      const updatedUser = result?.data;
      if (updatedUser) {
        try { dispatch({ type: actions.UPDATE_USER_COMPANY, payload: updatedUser }); } catch (_) {}
      }

      // Refresh auth context so the left card (which reads from useAuth) updates
      try { await refreshUser(); } catch (_) {}
      setIsEditing(false);
    } catch (e) {
      // Keep editing so user can retry; optionally surface error UI later
      console.error('Failed to update user', e);
    }
  };

  const handleCancel = () => {
    setFormData({
      firstName: profile.firstName || '',
      lastName: profile.lastName || '',
      email: user?.email || '',
      phone: profile.phone || '',
      bio: profile.bio || '',
      addresse: profile.addresse || '',
      gender: profile.gender || '',
      birthday: safeDate(profile.birthday),
      preferred_language: profile.preferred_language || 'fr',
    });
    setIsEditing(false);
  };

  const fullName = `${formData.firstName || profile.firstName || ''} ${formData.lastName || profile.lastName || ''}`.trim();

  return (
    <Box className="container" sx={{ mx: 'auto', py: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight={700}>
            My Bio
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage your personal information
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          {isEditing ? (
            <>
              <Button onClick={handleSave} variant="contained" startIcon={<SaveIcon />} disabled={isSaving}>
                {isSaving ? 'Saving...' : 'Save'}
              </Button>
              <Button onClick={handleCancel} variant="outlined" startIcon={<CloseIcon />}>
                Cancel
              </Button>
            </>
          ) : (
            <Button onClick={() => setIsEditing(true)} variant="contained" startIcon={<EditIcon />}>
              Edit
            </Button>
          )}
        </Stack>
      </Box>

      {/* Content */}
      <Grid container spacing={3} alignItems="stretch" direction="row" sx={{ flexWrap: { xs: 'wrap', md: 'nowrap' } }}>
        {/* Left: Overview */}
        <Grid item size={{ xs: 12, sm: 4, md: 4 }} sx={{ flexBasis: { xs: '100%', md: 400 }, maxWidth: { xs: '100%', md: 400 }, display: 'flex', flexDirection: 'column' }}>
          <Card sx={{ minWidth: 0, flex: 1 ,p:2, backgroundColor: theme.palette.background.secondCard }}>
            <Box sx={{ textAlign: 'center' }}>
              <Avatar
                sx={{ width: 96, height: 96, mx: 'auto', mb: 2, fontSize: 32 }}
                src={profile.profilePic || undefined}
                alt={fullName || user?.username || 'User'}
              >
                {initials}
              </Avatar>
              <Typography variant="h6">{fullName || '—'}</Typography>
              <Typography variant="body2" color="text.secondary">
                @{user?.username || '—'}
              </Typography>
              <Stack direction="row" spacing={1} justifyContent="center" sx={{ mt: 1 }}>
                <Chip label={user?.isActive ? 'Active' : 'Inactive'} color={user?.isActive ? 'success' : 'default'} size="small" />
                {user?.role?.type && <Chip label={user.role.type} variant="outlined" size="small" />}
              </Stack>
            </Box>
            <Stack spacing={1.5} sx={{ mt: 2 }}>
              <Stack direction="row" spacing={1.25} alignItems="center">
                <MailIcon fontSize="small" color="action" />
                <Typography variant="body2">{user?.email || '—'}</Typography>
              </Stack>
              <Stack direction="row" spacing={1.25} alignItems="center">
                <PhoneIcon fontSize="small" color="action" />
                <Typography variant="body2">{profile?.phone || '—'}</Typography>
              </Stack>
              <Stack direction="row" spacing={1.25} alignItems="center">
                <LocationOnIcon fontSize="small" color="action" />
                <Typography variant="body2">{profile?.addresse || '—'}</Typography>
              </Stack>
              <Stack direction="row" spacing={1.25} alignItems="center">
                <CalendarTodayIcon fontSize="small" color="action" />
                <Typography variant="body2">
                  {formData.birthday
                    ? new Date(formData.birthday).toLocaleDateString('en-GB')
                    : profile?.birthday
                    ? new Date(profile.birthday).toLocaleDateString('en-GB')
                    : '—'}
                </Typography>
              </Stack>
            </Stack>
          </Card>
        </Grid>

        {/* Right: Details */}
        <Grid item size={{ xs: 12 }} sx={{ flexGrow: 1, flexBasis: { xs: '100%', md: 0 }, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
          <Card sx={{ minWidth: 0, flex: 1  ,p:2,backgroundColor: theme.palette.background.secondCard}} subtitle="Edit your personal information" >
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                gap: 2,
                alignItems: 'start',
              }}
            >
              {/* First + Last */}
              <TextField
                label="First name"
                fullWidth
                value={formData.firstName}
                onChange={handleChange('firstName')}
                disabled={!isEditing}
              />
              <TextField
                label="Last name"
                fullWidth
                value={formData.lastName}
                onChange={handleChange('lastName')}
                disabled={!isEditing}
              />

              {/* Username + Email */}
              <TextField
                label="Username"
                fullWidth
                value={user?.username || ''}
                disabled
              />
              <TextField
                label="Email"
                type="email"
                fullWidth
                value={formData.email}
                onChange={handleChange('email')}
                disabled={!isEditing}
              />

              {/* Date of birth + Address */}
              <TextField
                label="Date of birth"
                type="date"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={formData.birthday}
                onChange={handleChange('birthday')}
                disabled={!isEditing}
              />
              <TextField
                label="Address"
                fullWidth
                value={formData.addresse}
                onChange={handleChange('addresse')}
                disabled={!isEditing}
              />

              {/* Phone + Gender */}
              <TextField
                label="Phone"
                fullWidth
                value={formData.phone}
                onChange={handleChange('phone')}
                disabled={!isEditing}
              />
              <Select
                fullWidth
                displayEmpty
                value={formData.gender || ''}
                disabled={!isEditing}
                onChange={handleChange('gender')}
              >
                <MenuItem value="">
                  <em>Gender</em>
                </MenuItem>
                <MenuItem value="Male">Male</MenuItem>
                <MenuItem value="Female">Female</MenuItem>
                <MenuItem value="Other">Other</MenuItem>
              </Select>

              {/* Bio full width */}
              <Box sx={{ gridColumn: { xs: '1', md: '1 / -1' } }}>
                <TextField
                  label="Biography"
                  fullWidth
                  multiline
                  minRows={4}
                  placeholder="Tell us about yourself..."
                  value={formData.bio}
                  onChange={handleChange('bio')}
                  disabled={!isEditing}
                />
              </Box>
            </Box>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}